/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import {ResponseData} from '@dvrd/dvr-controls';

import {useEffect, useRef, useState} from 'react';

import {OverviewListItem, OverviewPeriodRange, OverviewPeriodRequest, OverviewRequest} from '../../../util/interfaces';

type SectionRequest = (params: {
    data: OverviewRequest | OverviewPeriodRequest;
    callback: (data: ResponseData, response: Response) => void;
    errorCallback?: (response: Response) => void;
}) => unknown;

interface Config {
    request: SectionRequest;
    accountIds: Array<string>;
    /** Omitted by /overview/stock, which is not period-scoped. */
    period?: OverviewPeriodRange;
}

export interface OverviewSectionState<T extends OverviewListItem> {
    items: Array<T>;
    loading: boolean;
    loadingMore: boolean;
    error: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
}

/**
 * Fetch + page-number pagination shared by every section of the overview page:
 * all six endpoints take the same request shape and answer with `items` +
 * `has_more`. Changing the period or the selected accounts restarts the list
 * from the first page; "load more" requests the next page number (0-based —
 * the first page omits `page` entirely). The backend sizes the page itself:
 * five rows for page 0, ten for every page after it, so one click adds ten
 * rows in a single request.
 */
export function useOverviewSection<T extends OverviewListItem>(config: Config): OverviewSectionState<T> {
    const {request, accountIds, period} = config;
    const accountKey = accountIds.join(',');
    const [items, setItems] = useState<Array<T>>([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(accountIds.length > 0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(false);
    const requestSeq = useRef(0);
    const itemsRef = useRef<Array<T>>([]);
    const pageRef = useRef(0);
    // Reload on the dates themselves: the object identity changes on every
    // render of the page controller, the dates only when the user picks.
    const periodKey = period ? `${period.start_date}|${period.end_date}` : '';

    function load(page: number) {
        requestSeq.current += 1;
        const seq = requestSeq.current;
        if (page > 0) setLoadingMore(true);
        else setLoading(true);
        setError(false);
        const data: OverviewRequest & {period?: OverviewPeriodRange} = {account_ids: accountIds};
        if (period) data.period = period;
        if (page > 0) data.page = page;
        request({
            data: data as OverviewRequest | OverviewPeriodRequest,
            callback: (response: ResponseData) => {
                if (seq !== requestSeq.current) return;
                // `handled` means bluemateFetch already surfaced the reason (the
                // paused-subscription paywall) — a section error on top of that
                // dialog is noise.
                if (response.handled) {
                    setLoading(false);
                    setLoadingMore(false);
                    return;
                }
                if (response.success === false) {
                    setLoading(false);
                    setLoadingMore(false);
                    return setError(true);
                }
                const rows = (response.items ?? []) as Array<T>;
                const merged = page > 0 ? [...itemsRef.current, ...rows] : rows;
                itemsRef.current = merged;
                pageRef.current = page;
                setItems(merged);
                setHasMore(response.has_more === true);
                setLoading(false);
                setLoadingMore(false);
            },
            errorCallback: () => {
                if (seq !== requestSeq.current) return;
                setLoading(false);
                setLoadingMore(false);
                setError(true);
            }
        });
    }

    function onLoadMore() {
        if (loadingMore || !hasMore) return;
        load(pageRef.current + 1);
    }

    useEffect(() => {
        itemsRef.current = [];
        pageRef.current = 0;
        setItems([]);
        setHasMore(false);
        if (!accountIds.length) {
            requestSeq.current += 1;
            setLoading(false);
            setError(false);
            return;
        }
        load(0);
    }, [accountKey, periodKey]);

    return {items, loading, loadingMore, error, hasMore, onLoadMore};
}
